import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { EMPTY, pipe, exhaustMap, finalize } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  FLOW_GATEWAY,
  IWHCustomerDTO,
  IWHCustomerEndpointConfigModel,
  WHCustomerDataService,
} from '@workheld/workheld-shared-lib';
import { environment } from 'src/environments/environment';

type CustomersState = {
  customerMap: Record<string, IWHCustomerDTO>;
  isLoading: boolean;
  loaded: boolean;
};

const initialState: CustomersState = {
  customerMap: {},
  isLoading: false,
  loaded: false,
};

export const CustomersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    customers: computed(() => Object.values(store.customerMap())),
  })),
  withMethods((store, customerDataService = inject(WHCustomerDataService)) => ({
    getCustomerName(customerId: string): string {
      if (!customerId) return '-';
      return store.customerMap()[customerId]?.name ?? '-';
    },

    getCustomer(customerId: string): IWHCustomerDTO | undefined {
      if (!customerId) return undefined;
      return store.customerMap()[customerId];
    },

    getCustomerByUrl(detailURL: string): IWHCustomerDTO | undefined {
      if (!detailURL) return undefined;
      const id = extractCustomerIdFromUrl(detailURL);
      return id ? store.customerMap()[id] : undefined;
    },

    loadCustomers: rxMethod<void>(
      pipe(
        exhaustMap(() => {
          if (store.loaded() || store.isLoading()) return EMPTY;
          patchState(store, { isLoading: true });

          const endpointConfig: IWHCustomerEndpointConfigModel = {
            apiUrl: environment.apiUrl + FLOW_GATEWAY,
          };
          return customerDataService.getCustomerDtoList(endpointConfig).pipe(
            tapResponse(
              (customers: IWHCustomerDTO[]) => {
                const customerMap: Record<string, IWHCustomerDTO> = {};
                customers.forEach((c) => {
                  customerMap[c.id] = c;
                });
                patchState(store, {
                  customerMap,
                  isLoading: false,
                  loaded: true,
                });
              },
              () => {
                patchState(store, { isLoading: false });
              },
            ),
            finalize(() => patchState(store, { isLoading: false })),
          );
        }),
      ),
    ),
  })),
);

export function extractCustomerIdFromUrl(url: string): string | null {
  if (!url) return null;
  const segments = url.replace(/\/$/, '').split('/');
  return segments[segments.length - 1] || null;
}
